import { z } from "zod";

export const GpuTypeSchema = z.enum(["NVIDIA", "AMD", "Apple"]);

export type GpuType = z.infer<typeof GpuTypeSchema>;

export const GpuDetectedSchema = z.object({
  gpuId: z.string(),
  modelName: z.string(),
  vramBytes: z.number(),
  driverVersion: z.string(),
  type: GpuTypeSchema,
  // PCI IDs for reliable hardware identification
  pciVendorId: z.string().optional(),
  pciDeviceId: z.string().optional(),
});

export type GpuDetected = z.infer<typeof GpuDetectedSchema>;

// GPU slugs mapped directly from SMI product_name or AMD gpuModel slugified
export const GpuSlugEnum = z.enum([
  // Apple Silicon chips (slugified 'Apple Mx [Pro|Max|Ultra]')
  "apple-m1",
  "apple-m1-pro",
  "apple-m1-max",
  "apple-m1-ultra",
  "apple-m2",
  "apple-m2-pro",
  "apple-m2-max",
  "apple-m2-ultra",
  "apple-m3",
  "apple-m3-pro",
  "apple-m3-max",
  "apple-m3-ultra",
  "apple-m4",
  "apple-m4-pro",
  "apple-m4-max",

  // NVIDIA GeForce GPUs (slugified product_name)
  "nvidia-geforce-rtx-5090",
  "nvidia-geforce-rtx-5090-ti",
  "nvidia-geforce-rtx-5080",
  "nvidia-geforce-rtx-5080-ti",
  "nvidia-geforce-rtx-5080-laptop-gpu",
  "nvidia-geforce-rtx-5070-ti-laptop-gpu",
  "nvidia-geforce-rtx-5070-ti",
  "nvidia-geforce-rtx-5070",
  "nvidia-geforce-rtx-5060-ti",
  "nvidia-geforce-rtx-5060",
  "nvidia-geforce-rtx-5060-laptop-gpu",
  "nvidia-geforce-rtx-4090-d",
  "nvidia-geforce-rtx-4090-laptop-gpu",
  "nvidia-geforce-rtx-4090",
  "nvidia-geforce-rtx-4080-super",
  "nvidia-geforce-rtx-4080-laptop-gpu",
  "nvidia-geforce-rtx-4080",
  "nvidia-geforce-rtx-4070-super",
  "nvidia-geforce-rtx-4070",
  "nvidia-geforce-rtx-4070-ti",
  "nvidia-geforce-rtx-4070-ti-super",
  "nvidia-geforce-rtx-4070-laptop-gpu",
  "nvidia-geforce-rtx-4060-ti",
  "nvidia-geforce-rtx-4060-laptop-gpu",
  "nvidia-geforce-rtx-4060",
  "nvidia-geforce-rtx-4050-laptop-gpu",
  "nvidia-geforce-rtx-3090-ti",
  "nvidia-geforce-rtx-3090",
  "nvidia-geforce-rtx-3080-ti",
  "nvidia-geforce-rtx-3080",
  "nvidia-geforce-rtx-3080-laptop-gpu",
  "nvidia-geforce-rtx-3070-ti-laptop-gpu",
  "nvidia-geforce-rtx-3070-ti",
  "nvidia-geforce-rtx-3070-laptop-gpu",
  "nvidia-geforce-rtx-3070",
  "nvidia-geforce-rtx-3060-ti",
  "nvidia-geforce-rtx-3060",
  "nvidia-geforce-rtx-3060-laptop-gpu",
  "nvidia-geforce-rtx-3050-6gb-laptop-gpu",
  "nvidia-geforce-rtx-3050-laptop-gpu",
  "nvidia-geforce-rtx-3050",
  "nvidia-geforce-rtx-3050-ti",
  "nvidia-geforce-rtx-2080-ti",
  "nvidia-geforce-rtx-2080-super",
  "nvidia-geforce-rtx-2080",
  "nvidia-geforce-rtx-2070-super",
  "nvidia-geforce-rtx-2070",
  "nvidia-geforce-rtx-2060-super",
  "nvidia-geforce-rtx-2060",
  "nvidia-geforce-rtx-2050",
  "nvidia-geforce-gtx-1080",
  "nvidia-geforce-gtx-1080-ti",
  "nvidia-geforce-gtx-1070",
  "nvidia-geforce-gtx-1070-ti",
  "nvidia-geforce-gtx-1660",
  "nvidia-geforce-gtx-1660-ti",
  "nvidia-geforce-gtx-1660-super",
  "nvidia-geforce-gtx-1650",
  "nvidia-geforce-gtx-1650-ti",
  "nvidia-geforce-gtx-1650-super",
  "nvidia-geforce-gtx-1060",
  "nvidia-geforce-gtx-760",
  "nvidia-geforce-gtx-750-ti",
  "nvidia-geforce-gtx-730",
  "nvidia-geforce-940mx",
  "nvidia-geforce-210",

  "nvidia-geforce-rtx-4000",
  "nvidia-geforce-rtx-5000",
  "nvidia-geforce-rtx-6000",
  "nvidia-geforce-rtx-8000",
  "nvidia-rtx-a2000-12gb",
  "nvidia-rtx-a2000",
  "nvidia-rtx-a4000",
  "nvidia-rtx-a5000",
  "nvidia-rtx-a5000-laptop-gpu",
  "nvidia-rtx-a6000",
  "nvidia-rtx-6000-ada-generation",

  // NVIDIA Data Center GPUs (slugified product_name)
  "nvidia-l4",
  "nvidia-l40s",
  "nvidia-h200",
  "nvidia-h200-nvl",
  "nvidia-h200-pcie",
  "nvidia-h200-7c",
  "nvidia-gh200-480gb",
  "nvidia-rtx-pro-6000-blackwell-server-edition",
  "nvidia-h100-nvl",
  "nvidia-h100-pcie",
  "nvidia-h100",
  "nvidia-b40",
  "nvidia-b200",
  "nvidia-b300",
  // Grace Blackwell superchip GPUs (arm64 hosts; CoreWeave gb200-4x / gb300-4x)
  "nvidia-gb200",
  "nvidia-gb300",
  "nvidia-h100-sxm5-80gb",
  "nvidia-a2",
  "nvidia-a10",
  "nvidia-a16",
  "nvidia-a30",
  "nvidia-a40",
  "nvidia-a100-40gb",
  "nvidia-a100-80gb",
  "nvidia-a100-sxm4-40gb",
  "nvidia-a100-sxm4-80gb",
  "nvidia-a800-sxm4-40gb",
  "nvidia-a800-sxm4-80gb",
  "nvidia-m60",
  "nvidia-p100",
  "nvidia-p102-100",
  "nvidia-p104-100",
  "nvidia-p40",
  "nvidia-quadro-gp100",
  "nvidia-quadro-gv100",
  "nvidia-quadro-m2000m",
  "nvidia-quadro-m6000-24gb",
  "nvidia-quadro-p600",
  "nvidia-quadro-p5000",
  "nvidia-quadro-p5200",
  "nvidia-quadro-p6000",
  "nvidia-quadro-rtx-4000",
  "nvidia-quadro-rtx-5000",
  "nvidia-quadro-rtx-6000",
  "nvidia-t500",
  "nvidia-titan-rtx",
  "nvidia-titan-xp",
  "nvidia-cmp-50hx",
  "nvidia-rtx-5880-ada-generation",
  "nvidia-quadro-k2200",

  // AMD HPC GPUs (slugified gpuModel)
  "amd-mi100",
  "amd-mi200",
  "amd-mi210",
  "amd-mi250",
  "amd-mi250x",
  "amd-mi300",
  "amd-mi300a",
  "amd-mi300x",
  "amd-mi60",
  "amd-ssg",
  "amd-v340",
  "amd-vega-ii",
  "amd-vega-ii-duo",
  "amd-w6800",
  "amd-w6800x",
  "amd-w6800x-duo",
  "amd-w6900x",
  "amd-w7700",
  "amd-w7800",
  "amd-w7900",

  // AMD discrete GPUs (slugified gpuModel)

  "amd-radeon-rx-580-2048sp",
  "amd-radeon-rx-5600-xt",
  "amd-radeon-rx-5700",
  "amd-radeon-rx-5700-xt",
  "amd-radeon-rx-6600",
  "amd-radeon-rx-6700",
  "amd-radeon-rx-6700-xt",
  "amd-radeon-rx-6800",
  "amd-radeon-rx-6800-xt",
  "amd-radeon-rx-7600",
  "amd-radeon-rx-7600-xt",
  "amd-radeon-rx-vega-56",
  "amd-radeon-rx-vega-64",
  "amd-radeon-rx-7800-xt",
  "amd-radeon-rx-7900-xt",
  "amd-radeon-rx-7900-xtx",
  "amd-radeon-vii",

  // NVIDIA professional GPUs
  "nvidia-l20",
  "nvidia-rtx-a2000-12gb",
  "nvidia-rtx-a2000",
  "nvidia-rtx-a4000",
  "nvidia-rtx-a5000",
  "nvidia-rtx-a6000",
  "nvidia-quadro-rtx-4000",
  "nvidia-quadro-rtx-5000",
  "nvidia-cmp-50hx",
  "nvidia-cmp-100-210",
  "nvidia-rtx-2000-ada-generation-laptop-gpu",
  "nvidia-rtx-4000-ada-generation",
  "nvidia-rtx-4000-sff-ada-generation",
  "nvidia-rtx-4000-sff-ada-generation-laptop-gpu",
  "nvidia-rtx-4500-ada-generation",

  // NVIDIA Tesla GPUs (legacy naming)
  "nvidia-tesla-t4",
  "nvidia-tesla-p4",
  "nvidia-tesla-t10",
  "nvidia-tesla-m60",
  "nvidia-tesla-v100-12gb",
  "nvidia-tesla-v100-16gb",
  "nvidia-tesla-v100-32gb",
  "nvidia-tesla-v100s-32gb",

  // Integrated GPUs
  "intel-uhd-graphics",
  "intel-iris-xe-graphics",
  "amd-radeon-graphics",
  "amd-radeon-610m",
  "amd-radeon-760m-graphics",
  "microsoft-basic-display-adapter",

  // Undetectable GPUs
  "nvidia-graphics-device",
]);

export type GpuSlug = z.infer<typeof GpuSlugEnum>;

export type EngineConfigCards = Partial<Record<GpuSlug, number>>;

const BYTES_PER_GIB = 1024 ** 3;

/**
 * Advertised on-card VRAM (bytes) for a GPU slug, the way the vendor markets it.
 *
 * Keyed by the canonical {@link GpuSlug} via `satisfies`, so a typo or a slug
 * dropped from {@link GpuSlugEnum} fails the build instead of silently missing
 * the lookup. This is the single source of truth for "how much VRAM does a card
 * of slug X have" — display fallbacks and capacity estimates should read it via
 * {@link vramBytesForGpuSlug} rather than hard-coding their own tables.
 */
export const VRAM_BYTES_BY_GPU_SLUG = {
  "nvidia-a10": 24 * BYTES_PER_GIB,
  "nvidia-a100-40gb": 40 * BYTES_PER_GIB,
  "nvidia-a100-80gb": 80 * BYTES_PER_GIB,
  "nvidia-a100-sxm4-40gb": 40 * BYTES_PER_GIB,
  "nvidia-a100-sxm4-80gb": 80 * BYTES_PER_GIB,
  "nvidia-a800-sxm4-40gb": 40 * BYTES_PER_GIB,
  "nvidia-a800-sxm4-80gb": 80 * BYTES_PER_GIB,
  "nvidia-b40": 96 * BYTES_PER_GIB,
  "nvidia-b200": 192 * BYTES_PER_GIB,
  "nvidia-b300": 288 * BYTES_PER_GIB,
  "nvidia-gb200": 186 * BYTES_PER_GIB,
  "nvidia-gb300": 279 * BYTES_PER_GIB,
  "nvidia-geforce-rtx-3090": 24 * BYTES_PER_GIB,
  "nvidia-geforce-rtx-4090": 24 * BYTES_PER_GIB,
  "nvidia-geforce-rtx-5090": 32 * BYTES_PER_GIB,
  "nvidia-h100": 80 * BYTES_PER_GIB,
  "nvidia-h100-nvl": 94 * BYTES_PER_GIB,
  "nvidia-h100-pcie": 80 * BYTES_PER_GIB,
  "nvidia-h100-sxm5-80gb": 80 * BYTES_PER_GIB,
  "nvidia-h200": 143 * BYTES_PER_GIB,
  "nvidia-h200-7c": 143 * BYTES_PER_GIB,
  "nvidia-h200-nvl": 143 * BYTES_PER_GIB,
  "nvidia-h200-pcie": 143 * BYTES_PER_GIB,
  "nvidia-l4": 24 * BYTES_PER_GIB,
  "nvidia-l40s": 48 * BYTES_PER_GIB,
  "nvidia-rtx-a6000": 48 * BYTES_PER_GIB,
  "nvidia-rtx-pro-6000-blackwell-server-edition": 96 * BYTES_PER_GIB,
} satisfies Partial<Record<GpuSlug, number>>;

/**
 * Best-effort per-card VRAM estimate (bytes) for a GPU slug, for use when the
 * persisted `vramBytesPerCard` is missing or zero. Prefers the explicit
 * {@link VRAM_BYTES_BY_GPU_SLUG} table, then falls back to a `<n>gb` capacity
 * encoded in the slug itself. Returns `null` when neither yields a positive
 * value, so callers can decide how to render "unknown".
 */
export function vramBytesForGpuSlug(cardSlug: string): number | null {
  const known = (VRAM_BYTES_BY_GPU_SLUG as Record<string, number | undefined>)[
    cardSlug
  ];
  if (known != null) return known;

  const match = /(?:^|-)(\d+)gb(?:$|-)/i.exec(cardSlug);
  if (match?.[1] == null) return null;
  const gib = Number.parseInt(match[1], 10);
  return Number.isFinite(gib) && gib > 0 ? gib * BYTES_PER_GIB : null;
}

export const ALL_H100_GPUS: readonly GpuSlug[] = [
  "nvidia-h100",
  "nvidia-h100-nvl",
  "nvidia-h100-pcie",
  "nvidia-h100-sxm5-80gb",
];

export const ALL_H200_GPUS: readonly GpuSlug[] = [
  "nvidia-h200",
  "nvidia-h200-nvl",
  "nvidia-h200-pcie",
  "nvidia-h200-7c",
];

export const ALL_B40_GPUS: readonly GpuSlug[] = ["nvidia-b40"];

export const ALL_B200_GPUS: readonly GpuSlug[] = ["nvidia-b200"];

export const ALL_B300_GPUS: readonly GpuSlug[] = ["nvidia-b300"];

export const ALL_A100_80GB_GPUS: readonly GpuSlug[] = [
  "nvidia-a100-80gb",
  "nvidia-a100-sxm4-80gb",
];

export const ALL_A100_40GB_GPUS: readonly GpuSlug[] = [
  "nvidia-a100-40gb",
  "nvidia-a100-sxm4-40gb",
];

export const ALL_RTX_4090_GPUS: readonly GpuSlug[] = [
  "nvidia-geforce-rtx-4090",
  "nvidia-geforce-rtx-4090-d",
  "nvidia-geforce-rtx-4090-laptop-gpu",
];

export const ALL_RTX_5090_GPUS: readonly GpuSlug[] = [
  "nvidia-geforce-rtx-5090",
  "nvidia-geforce-rtx-5090-ti",
];

export type GpuFamily =
  | "h100"
  | "h200"
  | "b200"
  | "b300"
  | "b40"
  | "rtx-4090"
  | "rtx-5090";

/** Every GpuFamily, in canonical display order. Typed as a non-empty tuple of
 * the full union so `z.enum` can consume it and adding a family to `GpuFamily`
 * without listing it here fails the build. */
export const GPU_FAMILY_ORDER = [
  "h100",
  "h200",
  "b200",
  "b300",
  "b40",
  "rtx-4090",
  "rtx-5090",
] as const satisfies readonly [GpuFamily, ...GpuFamily[]];

/** Fails the build if a GpuFamily is missing from GPU_FAMILY_ORDER. */
type _AllFamiliesOrdered =
  Exclude<GpuFamily, (typeof GPU_FAMILY_ORDER)[number]> extends never
    ? true
    : never;
const _allFamiliesOrdered: _AllFamiliesOrdered = true;
void _allFamiliesOrdered;

export const GPU_FAMILY_LABELS: Record<GpuFamily, string> = {
  h100: "NVIDIA H100",
  h200: "NVIDIA H200",
  b200: "NVIDIA B200",
  b300: "NVIDIA B300",
  b40: "NVIDIA B40",
  "rtx-4090": "NVIDIA RTX 4090",
  "rtx-5090": "NVIDIA RTX 5090",
};

export const GPU_FAMILY_SLUGS: Record<GpuFamily, readonly GpuSlug[]> = {
  h100: ALL_H100_GPUS,
  h200: ALL_H200_GPUS,
  b200: ALL_B200_GPUS,
  b300: ALL_B300_GPUS,
  b40: ALL_B40_GPUS,
  "rtx-4090": ALL_RTX_4090_GPUS,
  "rtx-5090": ALL_RTX_5090_GPUS,
};

/**
 * GPU families a team may request in its own engine configs. Platform admins
 * are not limited to these — the model service enforces the rule and the
 * dashboard editor mirrors it.
 */
export const TEAM_ENGINE_CONFIG_GPU_FAMILIES: readonly GpuFamily[] = [
  "h100",
  "h200",
] as const;

/** Most cards of one type a team may request in its own engine config. */
export const MAX_TEAM_ENGINE_CONFIG_CARDS = 2;

export const GPU_FAMILY_BY_SLUG: Partial<Record<GpuSlug, GpuFamily>> = (() => {
  const out: Partial<Record<GpuSlug, GpuFamily>> = {};
  for (const family of GPU_FAMILY_ORDER) {
    for (const slug of GPU_FAMILY_SLUGS[family]) {
      out[slug] = family;
    }
  }
  return out;
})();

export const ALL_RTX_3090_GPUS: readonly GpuSlug[] = [
  "nvidia-geforce-rtx-3090",
  "nvidia-geforce-rtx-3090-ti",
];

// GPUs capable of high throughput (30+ TPS) on models like Llama 3.1 8B
export const HIGH_THROUGHPUT_GPUS: readonly GpuSlug[] = [
  // High-End Data Center GPUs (Definitely 30+ TPS)
  ...ALL_H200_GPUS,
  ...ALL_H100_GPUS,
  "nvidia-gh200-480gb",

  ...ALL_A100_80GB_GPUS,
  ...ALL_A100_40GB_GPUS,

  "nvidia-a800-sxm4-40gb",
  "nvidia-a800-sxm4-80gb",

  // Professional/Workstation GPUs (Likely 30+ TPS)
  "nvidia-geforce-rtx-5090",
  "nvidia-geforce-rtx-5080",

  ...ALL_RTX_4090_GPUS,

  "nvidia-geforce-rtx-4080",
  "nvidia-geforce-rtx-4080-super",
  "nvidia-geforce-rtx-4070",
  "nvidia-geforce-rtx-4070-super",
  "nvidia-geforce-rtx-4070",
  "nvidia-geforce-rtx-4070-ti",
  "nvidia-geforce-rtx-4070-ti-super",
  "nvidia-l40s",
  "nvidia-a40",
  "nvidia-geforce-rtx-6000",
  "nvidia-rtx-a6000",
  "nvidia-rtx-5880-ada-generation",

  // Apple Silicon (Likely 30+ TPS)
  "apple-m2-pro",
  "apple-m2-max",
  "apple-m2-ultra",
  "apple-m3-pro",
  "apple-m3-max",
  "apple-m3-ultra",
  "apple-m4-max",
  "apple-m4-pro",
  "apple-m4",

  // Mid-Range GPUs (Possibly 30+ TPS)
  ...ALL_RTX_3090_GPUS,
  "nvidia-geforce-rtx-3080-ti",
  "nvidia-l4",
  "nvidia-a30",
  "nvidia-a10",
];

// PCI ID database for GPU model identification
// Format: vendorId:deviceId -> GpuSlug
export const PCI_ID_TO_GPU_SLUG: Record<string, GpuSlug> = {
  // NVIDIA GPUs (vendor 0x10de)
  // RTX 40 Series
  "0x10de:0x2684": "nvidia-geforce-rtx-4090",
  "0x10de:0x2702": "nvidia-geforce-rtx-4080",
  "0x10de:0x2705": "nvidia-geforce-rtx-4070-ti",
  "0x10de:0x2782": "nvidia-geforce-rtx-4070",
  "0x10de:0x2786": "nvidia-geforce-rtx-4070",
  "0x10de:0x2704": "nvidia-geforce-rtx-4070-ti-super",
  "0x10de:0x2709": "nvidia-geforce-rtx-4070-super",
  "0x10de:0x2803": "nvidia-geforce-rtx-4060-ti",
  "0x10de:0x2882": "nvidia-geforce-rtx-4060",

  // RTX 30 Series
  "0x10de:0x2204": "nvidia-geforce-rtx-3090",
  "0x10de:0x2208": "nvidia-geforce-rtx-3090-ti",
  "0x10de:0x2206": "nvidia-geforce-rtx-3080",
  "0x10de:0x2216": "nvidia-geforce-rtx-3080-ti",
  "0x10de:0x2520": "nvidia-geforce-rtx-3060-laptop-gpu",
  "0x10de:0x2460": "nvidia-geforce-rtx-3080-laptop-gpu",
  "0x10de:0x2484": "nvidia-geforce-rtx-3070",
  "0x10de:0x2482": "nvidia-geforce-rtx-3070-ti",
  "0x10de:0x2503": "nvidia-geforce-rtx-3060",
  "0x10de:0x2504": "nvidia-geforce-rtx-3060",
  "0x10de:0x2414": "nvidia-geforce-rtx-3060-ti",

  "0x10de:0x1f82": "nvidia-geforce-gtx-1650",

  // GTX 10 Series
  "0x10de:0x1b81": "nvidia-geforce-gtx-1070",

  // GTX 700 Series
  "0x10de:0x1187": "nvidia-geforce-gtx-760",

  // Data Center GPUs
  "0x10de:0x20b0": "nvidia-a100-40gb",
  "0x10de:0x20b2": "nvidia-a100-80gb",
  "0x10de:0x20f1": "nvidia-a100-40gb",
  "0x10de:0x2330": "nvidia-h100",
  "0x10de:0x2331": "nvidia-h100-pcie",
  "0x10de:0x2339": "nvidia-h100-nvl",
  "0x10de:0x2335": "nvidia-h200",
  "0x10de:0x2235": "nvidia-a40",
  "0x10de:0x2236": "nvidia-a10",
  "0x10de:0x25b6": "nvidia-a16",
  "0x10de:0x20b7": "nvidia-a30",
  "0x10de:0x27b8": "nvidia-l4",
  "0x10de:0x26b5": "nvidia-l40s",
  "0x10de:0x27b0": "nvidia-rtx-4000-ada-generation",
  "0x10de:0x1f02": "nvidia-t500",
  "0x10de:0x1eb8": "nvidia-quadro-rtx-5000",
  "0x10de:0x1eb1": "nvidia-quadro-rtx-4000",
  "0x10de:0x13f2": "nvidia-quadro-m2000m",
  "0x10de:0x1cb1": "nvidia-quadro-p600",
  "0x10de:0x1b00": "nvidia-titan-xp",
  "0x10de:0x1e30": "nvidia-tesla-t4",
  "0x10de:0x1bb3": "nvidia-tesla-p4",
  "0x10de:0x1df0": "nvidia-tesla-v100-12gb",
  "0x10de:0x1db1": "nvidia-tesla-v100-16gb",
  "0x10de:0x1db4": "nvidia-tesla-v100-32gb",
  "0x10de:0x1db5": "nvidia-tesla-v100s-32gb",
  "0x1002:0x744c": "amd-radeon-rx-7900-xtx",
  "0x1002:0x7448": "amd-radeon-rx-7800-xt",
  "0x1002:0x747e": "amd-radeon-rx-7600",
  "0x1002:0x7480": "amd-radeon-rx-7600-xt",
  "0x1002:0x73bf": "amd-radeon-rx-6800",
  "0x1002:0x73df": "amd-radeon-rx-6700-xt",
  "0x1002:0x73ff": "amd-radeon-rx-6600",
  "0x1002:0x73ef": "amd-radeon-rx-6800-xt",
  "0x1002:0x731f": "amd-radeon-rx-5700-xt",
  "0x1002:0x7340": "amd-radeon-rx-5700",
  "0x1002:0x7341": "amd-radeon-rx-5600-xt",
  "0x1002:0x687f": "amd-radeon-rx-vega-64",
  "0x1002:0x6863": "amd-radeon-rx-vega-56",
  "0x1002:0x73a5": "amd-w6800",
  "0x1002:0x73e0": "amd-w6800x",
  "0x1002:0x73e1": "amd-w6800x-duo",
  "0x1002:0x73a0": "amd-w6900x",
  "0x1002:0x738c": "amd-mi100",
  "0x1002:0x740c": "amd-mi210",
  "0x1002:0x740f": "amd-mi250x",
  "0x1002:0x7408": "amd-mi250",
  "0x1002:0x74a0": "amd-mi300x",
  "0x1002:0x74a1": "amd-mi300a",
};
